import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { Box, Button, Flex, Image, Text, useDisclosure } from "@chakra-ui/react";
import { getApi, deleteApi } from "common/services/api";
import { HasAccess } from "../../../../common/utils/accessUtils";
import { getLoggedInUser, isSuperAdmin as checkIsSuperAdmin } from "common/utils/roleUtils";
import CommonCheckTable from "common/components/checktable";
import { SearchIcon } from "@chakra-ui/icons";
import { useLocation, useNavigate } from "react-router-dom";
import CommonDeleteModel from "common/components/commonDeleteModel";
import { toast } from "react-toastify";

import ActionMenu from "common/components/ActionMenu";
import {
  getTableFilters,
  saveTableFilters,
  clearTableFilters,
  getInitialFilterState
} from "common/utils/filterUtils";
import { constant } from "common/constant";
import { globalStyles } from "common/theme/styles";
import { referralsData } from "services/Recruit/data/referrals";
import { recruitConstant } from "services/Recruit/constant";
import { accessModuleTitles } from "common/constant";
import { deleteManyApi } from "common/services/api";
import { formatJobTitle } from "services/Crm/utils/calcUtils";
import { formatDateTime } from "common/utils/dateFormater";
import { useDispatch } from "react-redux";
import { deleteMultipleReferral } from "common/redux/reducers/referralSlice";
import { API_PATHS } from "common/services/apiPaths";
import InfoModal from "common/components/infoModel";

const Referals = () => {
  const title = recruitConstant.createReferral;
  const TABLE_NAME = 'referrals'; // Define table name constant
  const [action, setAction] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [deleteMany, setDeleteMany] = useState(false);
  const [selectedValues, setSelectedValues] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([])
  const [tableData, setTableData] = useState(referralsData);
  const [searchboxOutside, setSearchboxOutside] = useState("");
  const [displaySearchData, setDisplaySearchData] = useState(false);
  const [searchedData, setSearchedData] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const colors = globalStyles.colors;

  const [referralsPermission, myReferralsPermission] = HasAccess([
    accessModuleTitles.Referrals,
    accessModuleTitles.MyReferrals,
  ]);
  const permission = referralsPermission || myReferralsPermission;

  const loggedInUser = getLoggedInUser();
  const isSuperAdmin = checkIsSuperAdmin(loggedInUser);

  const getActiveRoleName = useCallback((user) => {
    const direct = user?.activeRoleId?.roleName;
    if (direct) return String(direct).toLowerCase();
    const activeRoleId = user?.activeRoleId?._id || user?.activeRoleId;
    const roles = Array.isArray(user?.roles) ? user.roles : [];
    if (activeRoleId && roles.length) {
      const found = roles.find((r) => String(r?._id || r) === String(activeRoleId));
      const name = found?.roleName || found?.role;
      if (name) return String(name).toLowerCase();
    }
    return String(user?.role || '').toLowerCase();
  }, []);

  const isEmployeeActiveRole = getActiveRoleName(loggedInUser) === 'employee';
  const tableAccess = useMemo(() => {
    if (!isEmployeeActiveRole) return permission;
    return { ...(permission || {}), create: true, view: true, update: true };
  }, [isEmployeeActiveRole, permission]);

  const formatExperience = useCallback((experience) => {
    if (experience === undefined || experience === null || experience === "") return "-";

    const value = String(experience).trim();
    if (!value) return "-";

    if (/year/i.test(value)) return value;

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return `${value} ${numericValue === 1 ? "year" : "years"}`;
    }

    return value;
  }, []);

  const getId = useCallback((value) => (value?._id ? value._id : value), []);

  const [currentPage, setCurrentPage] = useState(() => {
    const paginationState = JSON.parse(localStorage.getItem("paginationState"))
    return paginationState?.pageName === title && paginationState?.currentPage ? paginationState.currentPage : 1
  })

  const [jobOpenings, setJobOpenings] = useState([])
  const [isJobOpeningsLoading, setIsJobOpeningsLoading] = useState(true);
  const hasMatchingJobOpening = useCallback(
    (jobOpeningValue) => {
      const jobOpeningId = String(getId(jobOpeningValue) || "");
      if (!jobOpeningId) return false;

      return jobOpenings.some((job) => String(getId(job)) === jobOpeningId);
    },
    [getId, jobOpenings]
  );

  const [pageSize, setPageSize] = useState(() => {
    const paginationState = JSON.parse(localStorage.getItem("paginationState"))
    return paginationState?.pageName === title && paginationState?.pageSize ? paginationState.pageSize : 10
  })
  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState("")
  const [searchbox, setSearchbox] = useState(() => {
    const filters = getInitialFilterState(TABLE_NAME);
    return filters?.searchbox || null;
  });

  // Save filters whenever they change
  useEffect(() => {
    const filters = {
      searchbox: searchbox || "",
    };
    saveTableFilters(TABLE_NAME, filters);
  }, [searchbox]);
  const [finalSearchedData, setFinalSearchedData] = useState([])
  const [metadata, setMetadata] = useState({})
  const [menuSelectedDeleteItem, setMenuSelectedDeleteItem] = useState([])
  const [isDeleteInfoVisible, setIsDeleteInfoVisible] = useState(false)
  const dispatch = useDispatch();

  // Use ref to track if this is initial render
  const isInitialMount = useRef(true);

  // Fetch job openings on mount
  useEffect(() => {
    const fetchJobOpenings = async () => {
      setIsJobOpeningsLoading(true);
      try {
        const result = await getApi(`${API_PATHS.RECRUITMENT_PROFILES}?allData=true`);
        setJobOpenings(result?.data?.data || []);
      } catch (e) {
        console.error("Failed to fetch job openings:", e);
        toast.error("Failed to load job openings");
      } finally {
        setIsJobOpeningsLoading(false);
      }
    };

    fetchJobOpenings();
  }, []);

  // Fetch data on mount and pagination
  useEffect(() => {
    fetchData(currentPage, pageSize, sortColumn, sortOrder, searchbox || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortColumn, sortOrder]);

  // Navigation handlers - Don't include navigate in dependencies to avoid recreating
  const handleNavigateToReferral = useCallback((referralId, mode = "overview") => {

    if (!isEmployeeActiveRole && isJobOpeningsLoading) {
      // toast.info("Please wait, loading data...");
      return;
    }

    const referralData = data.find(item => item._id === referralId);

    // Employees might not have permission to fetch the job opening list; don't block navigation/edit in that case.
    if (!isEmployeeActiveRole) {
      const jobExists = hasMatchingJobOpening(referralData?.referringForJob);
      if (!jobExists) {
        setIsDeleteInfoVisible(true);
        return;
      }
    }

    sessionStorage.setItem("mode", mode);
    navigate(`/recruitment/referral-create/${referralId}`);
  }, [isEmployeeActiveRole, isJobOpeningsLoading, data, hasMatchingJobOpening, navigate]);

  const handleNavigateToJobOpening = useCallback((jobOpeningId, mode = "overview") => {

    if (isJobOpeningsLoading) {
      // toast.info("Please wait, loading data...");
      return;
    }

    const jobExists = hasMatchingJobOpening(jobOpeningId);

    if (!jobExists) {
      setIsDeleteInfoVisible(true);
      return;
    }

    sessionStorage.setItem("mode", mode);
    navigate(`/recruitment/job-opening-create/${jobOpeningId}`);
  }, [hasMatchingJobOpening, isJobOpeningsLoading, navigate]);

  const canSelectRow = useCallback((rowData) => {
    const isCreator = String(getId(rowData?.createdBy)) === String(loggedInUser?._id);
    return tableAccess?.delete && (isSuperAdmin || isCreator);
  }, [getId, loggedInUser?._id, isSuperAdmin, tableAccess?.delete]);

  // Action header
  const actionHeader = useMemo(() => ({
    Header: recruitConstant.actions,
    isSortable: false,
    center: true,
    disableRowClick: true,
    cell: ({ row }) => {
      const referralData = data.find(item => item._id === row?.values?._id);
      const jobExists = isEmployeeActiveRole ? true : hasMatchingJobOpening(referralData?.referringForJob);
      const isCreator = String(getId(row?.original?.createdBy)) === String(loggedInUser?._id);
      const canDelete = tableAccess?.delete && (isSuperAdmin || isCreator);
      const canEdit = tableAccess?.update && jobExists && (isSuperAdmin || isCreator);

      return (
        <ActionMenu
          canView={true}
          canEdit={canEdit}
          canDelete={canDelete}
          onView={() => handleNavigateToReferral(row?.values?._id, "overview")}
          onEdit={() => {
            sessionStorage.setItem("mode", 'edit');
            handleNavigateToReferral(row?.values?._id, "edit");
          }}
          onDelete={() => {
            setSelectedValues([row?.values?._id]);
            setMenuSelectedDeleteItem([row?.values?._id]);
            setDeleteMany(true);
          }}
        />
      );
    },
  }), [getId, handleNavigateToReferral, hasMatchingJobOpening, isEmployeeActiveRole, loggedInUser?._id, isSuperAdmin, tableAccess, data]);

  // Table columns - memoized with stable dependencies
  const tableColumns = useMemo(() => {

    return [
      {
        Header: "#",
        accessor: "_id",
        isSortable: false,
        width: 5,
      },
      {
        Header: recruitConstant.candidateName,
        accessor: "name",
        cell: ({ row }) => (
          <Text
            as="span"
            fontSize="sm"
            fontWeight="400"
            color={row?.values?.name ? colors.cta["100"] : "inherit"}
            cursor={row?.values?.name ? "pointer" : "default"}
            _hover={row?.values?.name ? { textDecoration: "underline", color: colors.cta["100"] } : undefined}
            onClick={() => handleNavigateToReferral(row?.values?._id, "overview")}
            whiteSpace="nowrap"
          >
            {row?.values?.name || '-'}
          </Text>
        ),
      },
      {
        Header: recruitConstant.jobApplied,
        accessor: "referringForJob",
        cell: ({ row }) => (
          <Flex alignItems="center" gap={2}>
            <Text
              fontSize="md"
              fontWeight="500"
              color={!isEmployeeActiveRole && row?.values?.referringForJob?._id ? colors.textPrimary : colors.textPrimary}
              cursor={!isEmployeeActiveRole && row?.values?.referringForJob?._id ? "pointer" : "default"}
              onClick={() => {
                if (isEmployeeActiveRole) return;
                const jobId = row?.values?.referringForJob?._id;
                if (jobId) {
                  handleNavigateToJobOpening(jobId, "overview");
                }
              }}
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              overflow="hidden"
            >
              {formatJobTitle(row?.values?.referringForJob?.postingTitle) ||
                row?.values?.referringForJob?.customId ||
                recruitConstant.notAvailable}
            </Text>
            {(row?.values?.referringForJob?.postingTitle && row?.values?.referringForJob?.customId) && (
              <Text fontSize="md" color={colors.textPrimary}>
                ({row?.values?.referringForJob?.customId})
              </Text>
            )}
          </Flex>
        ),
      },
      (!isEmployeeActiveRole && {
        Header: recruitConstant.referredBy,
        accessor: "createdBy",
        cell: ({ row }) => (
          <Text
            fontSize="md"
            fontWeight="500"
            color={colors.textPrimary}
            cursor={"pointer"}
            onClick={() => handleNavigateToReferral(row?.values?._id, "overview")}
            className="cursor-pointer"
          >
            {row?.values?.createdBy?.firstName} {row?.values?.createdBy?.lastName}
          </Text>
        ),
      }),
      {
        Header: recruitConstant.currentEmployer,
        accessor: "currentEmployer"
      },
      {
        Header: isEmployeeActiveRole ? recruitConstant.experience : "Experience",
        accessor: "experience",
        cell: ({ row }) => (
          <Text fontSize="md" fontWeight="500" color={colors.textPrimary}>
            {formatExperience(row?.original?.experience)}
          </Text>
        )
      },
      ...(tableAccess?.update || tableAccess?.delete ? [actionHeader] : []),
    ].filter(Boolean);
  }, [handleNavigateToReferral, handleNavigateToJobOpening, actionHeader, colors, tableAccess, isEmployeeActiveRole, formatExperience]);

  const employeePreferredHeaders = [
    "#",
    recruitConstant.candidateName,
    recruitConstant.jobApplied,
    recruitConstant.experience,
    recruitConstant.jobtitle,
    recruitConstant.currentEmployer,
    recruitConstant.actions,
  ];

  // Directly use tableColumns for columns and selectedColumns
  const [selectedColumns, setSelectedColumns] = useState([])

  // Initialize selectedColumns only once
  useEffect(() => {
    if (isInitialMount.current && tableColumns.length > 0) {
      setSelectedColumns(
        isEmployeeActiveRole
          ? employeePreferredHeaders
              .map((header) => tableColumns.find((column) => column?.Header === header))
              .filter(Boolean)
          : [...tableColumns]
      );
      isInitialMount.current = false;
    }
  }, [tableColumns, isEmployeeActiveRole]);

  // Memoize dataColumn
  const dataColumn = useMemo(() =>
    selectedColumns
      ?.map((selectedColumn) =>
        tableColumns?.find((item) => item?.Header === selectedColumn?.Header)
      )
      .filter(Boolean),
    [tableColumns, selectedColumns]
  );

  const fetchData = async (
    page = currentPage,
    limit = pageSize,
    sortKey = sortColumn,
    order = sortOrder,
    searchTerm = "",
    fetchAllData = false,
    status = "",
  ) => {
    setIsLoading(true)

    let endpoint = `api/recruitment/referral/?page=${page}&limit=${limit}`

    if (fetchAllData) {
      endpoint = `api/recruitment/referral/?allData=true`
    }

    if (sortKey) {
      const sortParam = order === "desc" ? `-${sortKey}` : sortKey
      endpoint += `&sort=${sortParam}`
    }

    if (searchTerm) {
      endpoint += `&search=${encodeURIComponent(searchTerm)}`
    }

    if (status) {
      endpoint += `&status=${encodeURIComponent(status)}`
    }

    try {
      const result = await getApi(endpoint)
      if (fetchAllData) {
        setFinalSearchedData(result?.data?.data)
      } else {
        setData(result?.data?.data || [])
        setMetadata(result?.data?.metadata)
      }
    } catch (e) {
      toast.error(recruitConstant.errorMsg)
      console.error("Error fetching referral:", e)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSearchChange = (term) => {
    const trimmedTerm = term?.trim() || "";
    setSearchbox(trimmedTerm);

    const pageToFetch = trimmedTerm === "" ? currentPage : 1
    if (trimmedTerm !== "" && currentPage !== 1) {
      setCurrentPage(1);
    }
    fetchData(pageToFetch, pageSize, sortColumn, sortOrder, trimmedTerm, false, "")
  }

  const handleSortChange = (accessorKey) => {
    let newSortOrder = "asc"

    if (sortColumn === accessorKey) {
      newSortOrder = sortOrder === "asc" ? "desc" : "asc"
    }

    setSortColumn(accessorKey)
    setSortOrder(newSortOrder)
    fetchData(currentPage, pageSize, accessorKey, newSortOrder, searchbox, false, "")
  }


  const handleDeleteReferral = async (ids) => {
    try {
      setDeleteMany(false)

      const result = await dispatch(deleteMultipleReferral(ids))

      if (result.type.endsWith("/fulfilled")) {
        setSelectedValues([])
        fetchData(currentPage, pageSize)
        toast.success(
          `${menuSelectedDeleteItem.length > 0
            ? recruitConstant.referral
            : selectedValues && selectedValues.length > 1
              ? `${recruitConstant.referral}s`
              : recruitConstant.referral
          } ${recruitConstant.deletedSuccessfully}`,
        )

        setAction((pre) => !pre)
      }
    } catch (error) {
      toast.error("Something went wrong")
    } finally {
      setMenuSelectedDeleteItem([])
    }
  }

  const onLinkClick = useCallback((id) => {
    handleNavigateToReferral(id, "overview");
  }, [handleNavigateToReferral]);

  return (
    <div>
      <CommonCheckTable
        title={title}
        showInlineTitle={true}
        inlineTitle={
          isEmployeeActiveRole
            ? `My Referrals (${metadata?.totalRecords ?? data?.length ?? 0})`
            : `Referrals (${metadata?.totalRecords ?? data?.length ?? 0})`
        }
        isLoading={isLoading || isJobOpeningsLoading}
        columnData={tableColumns}
        dataColumn={dataColumn}
        allData={data}
        tableData={data}
        setSearchDisplay={setDisplaySearchData}
        tableCustomFields={[]}
        onLinkClick={onLinkClick}
        access={tableAccess}
        action={action}
        setAction={setAction}
        disableDefaultCellNavigation={isEmployeeActiveRole}
        checkBox={canSelectRow}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        isOpen={isOpen}
        setCurrentPage={setCurrentPage}
        fetchData={fetchData}
        currentPage={currentPage}
        onClose={onclose}
        onOpen={onOpen}
        selectedValues={selectedValues}
        setSelectedValues={setSelectedValues}
        setDelete={setDeleteMany}
        searchboxOutside={searchboxOutside}
        setSearchboxOutside={setSearchboxOutside}
        sortOrder={sortOrder}
        searchbox={searchbox}
        handleSortChange={handleSortChange}
        handleSearchChange={handleSearchChange}
        setSearchbox={setSearchbox}
        finalSearchedData={finalSearchedData}
        metadata={metadata}
        setFinalSearchedData={setFinalSearchedData}
        pageSize={pageSize}
        setPageSize={setPageSize}
        isDynamic={true}
      />

      <CommonDeleteModel
        isOpen={deleteMany}
        onClose={() => setDeleteMany(false)}
        type={
          menuSelectedDeleteItem.length > 0
            ? recruitConstant.referral
            : selectedValues && selectedValues.length > 1
              ? `${recruitConstant.referral}s`
              : recruitConstant.referral
        }
        handleDeleteData={handleDeleteReferral}
        ids={selectedValues}
        onCancel={() => setSelectedValues([])}
      />
      <InfoModal
        onClose={() => setIsDeleteInfoVisible(false)}
        isOpen={isDeleteInfoVisible}
        message={'The job opening related to this referral has already been deleted.'}
      />
    </div>
  );
};

export default Referals;
